import { type MouseEvent, type ReactNode } from 'react';

import {
  Box,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import Drawer from '@/components/wrapped/Drawer';
import StatusBar from '@/designs-dev/device/StatusBar';
import CareSearch from '@/cam-admin/care-search/CareSearch';
// The flow starts on the real CAM shortlist, which is a tab in the control
// panel. Rendered as itself rather than redrawn — same reasoning as the
// member-side frames below.
import ControlPanel from '@/cam-admin/control-panel/ControlPanel';
import { type ShortlistInitial } from '@/cam-admin/paywall/useShortlists';
// The member-side frames reuse the published-shortlist design rather than
// redrawing it. This flow crosses products by nature — the whole argument is
// that a payment on the CAM side decides what appears on the member's phone.
import ShortlistView from '@/karehero-app/care/shortlists/ShortlistView';
import { SHORTLISTS } from '@/karehero-app/care/shortlists/fixtures';
import { FLOW } from '@/cam-admin/explore/paywall/Paywall.content';

// The blurred-profile paywall, end to end. Sketch fidelity — enough to argue
// about the order of the steps and where money changes hands.

// The answer frames are static: they show a state, not a working form, so the
// toggles are controlled with nothing behind them.

// The desktop scaffold every frame sits in, so seven sketches share one chrome.
// `position: relative` so the wrapped Drawer, which is an in-tree overlay
// anchored to its parent, lands inside the page rather than the whole frame.
const Page = ({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  /** Page-level action, pinned to the right of the header. */
  action?: ReactNode;
  children: ReactNode;
}) => (
  <Stack
    sx={{ height: '100%', bgcolor: 'background.default', position: 'relative' }}
  >
    <Box
      sx={{
        px: 6,
        py: 3,
        borderBottom: 1,
        borderColor: 'custom.neutral.200',
        flexShrink: 0,
      }}
    >
      <Stack
        sx={{ mx: 'auto', alignItems: 'center', maxWidth: 1200 }}
        direction='row'
        spacing={2}
      >
        <Box
          sx={{ width: 40, height: 'auto' }}
          component='img'
          src='/vector/karehero-logo.png'
          alt='KareHero'
        />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant='h3'>{title}</Typography>
          {subtitle && (
            <Typography variant='body1' color='text.secondary'>
              {subtitle}
            </Typography>
          )}
        </Stack>
        {action}
      </Stack>
    </Box>
    {/* Scrolls rather than clips. The answers page is deliberately longer than
        one screen — and how long it is happens to be the question that frame is
        asking — so hiding the overflow would hide the argument. The thin
        only-when-overflowing scrollbar is the global rule in styles.css. */}
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 6, py: 5 }}>
      {/* Content caps at 1200 and centres, per the CAM Backend layout in Figma
          (node 70-10703). The scroll and the gutters stay on the parent, so a
          wide monitor gets more margin rather than a longer line of text — a
          420 rail against a card that keeps growing reads as two unrelated
          columns. */}
      <Box sx={{ mx: 'auto', maxWidth: 1200 }}>{children}</Box>
    </Box>
  </Stack>
);

// Owns the blur so it never lands as an sx at a call site. Rendering the real
// value underneath is deliberate: the provider should see the shape of what
// they are buying, not an empty box.
const Blurred = ({ children }: { children: ReactNode }) => (
  <Box
    aria-hidden
    sx={{ filter: 'blur(6px)', userSelect: 'none', pointerEvents: 'none' }}
  >
    {children}
  </Box>
);

// A label and its value. The value is body, not a heading: a card of eight
// headings has no heading, and the lead details are there to be read in order
// rather than scanned past the title. The overline label is what separates the
// two lines, so the value doesn't need weight to sit apart from it.
const Field = ({ label, value }: { label: string; value: ReactNode }) => (
  <Stack spacing={0.5}>
    <Typography variant='overline' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='body1'>{value}</Typography>
  </Stack>
);

// The shortlist page itself, shared by A2 and by the drawer frame so the two
// can't drift. `openRow` names the row the drawer is open on, which is also
// the row that reads as selected.
const ShortlistPage = ({
  openRow,
  children,
}: {
  openRow?: string;
  children?: ReactNode;
}) => {
  const c = FLOW.send;
  return (
    <Page
      title={c.title}
      subtitle={c.subtitle}
      action={
        <Button ariaLabel={c.publish} size='md'>
          {c.publish}
        </Button>
      }
    >
      <Paper variant='outlined'>
        <Table>
          <TableHead>
            <TableRow>
              {c.columns.map((h) => (
                <TableCell key={h}>{h}</TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {c.rows.map((r) => (
              <TableRow key={r.name} hover selected={r.name === openRow}>
                <TableCell>{r.name}</TableCell>
                <TableCell>{r.tier}</TableCell>
                <TableCell>{r.sent}</TableCell>
                <TableCell>
                  <Chip size='small' label={r.status} />
                </TableCell>
                <TableCell align='right'>
                  {r.status === 'Not sent' && (
                    <Button ariaLabel={c.cta} variant='secondary' size='sm'>
                      {c.cta}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Typography sx={{ mt: 3 }} variant='body2' color='text.secondary'>
        {c.note}
      </Typography>
      {children}
    </Page>
  );
};

// A2 — the shortlist as its own page, full width. Kept for the hidden
// rail-vs-page section; the flow itself uses the real Shortlists tab.
export const A2ShortlistPage = () => <ShortlistPage />;
A2ShortlistPage.displayName = 'A2ShortlistPage';

// The care circle as it ships, opened on a given tab. The frames that show a CAM
// at work are the real screens rather than sketches of them: a shortlist belongs
// to a circle, so it lives on a tab beside Calls and Documents, and redrawing it
// here would quietly argue for a page of its own. Same reasoning as the
// member-side frames — the argument is about who reaches the list, not what the
// list looks like.
const CamCircle = ({
  tab,
  shortlist,
}: {
  tab: 'care-search' | 'shortlist';
  shortlist?: ShortlistInitial;
}) => (
  <ControlPanel embedded initialTab={tab} withShortlist shortlist={shortlist} />
);

// F0 — the assessment lands and the CAM builds the list. Care search, because
// that is where a provider gets onto a shortlist in the first place.
export const F0BuildShortlist = () => <CamCircle tab='care-search' />;
F0BuildShortlist.displayName = 'F0BuildShortlist';

// F1b — a row opened. The drawer is where the round of calls ended up: the
// provider's own answers, in writing, on the row that paid for the lead.
// `firstPaid` with the paid row open, because a row nobody has answered has
// nothing in the half of the drawer this frame is about.
export const F1bRowDrawer = () => (
  <CamCircle
    tab='shortlist'
    shortlist={{ scenario: 'firstPaid', openRow: 'oakwood-care-home' }}
  />
);
F1bRowDrawer.displayName = 'F1bRowDrawer';

// F1 — the send, and the waiting that follows it. This used to be the round of
// calls, and the 12 Aug spec deleted it: the care request goes out carrying the
// anonymised profile, the provider answers on the page, and whoever pays fills
// the shortlist. The Shortlists tab does both jobs, so it is one frame rather
// than two.
//
// `someSent` rather than a draft: it has a provider added after the request went
// out, so the row that never got the first send sits next to three that did, which
// is the one shape the page-level action cannot fix. F9 picks up the paid end of
// the lifecycle.
export const F1SendRequest = () => (
  <CamCircle tab='shortlist' shortlist={{ scenario: 'someSent' }} />
);
F1SendRequest.displayName = 'F1SendRequest';

// B2 — a row opened. Everything the table doesn't carry, including the note
// that pushed providers out of view when the shortlist lived in the rail.
export const B2RowDrawer = () => {
  const c = FLOW.rowDrawer;
  return (
    <ShortlistPage openRow={c.provider}>
      <Drawer
        open
        title={c.provider}
        subtitle={c.subtitle}
        onClose={() => {}}
        footer={
          <Button ariaLabel={c.cta} size='lg' isFullWidth>
            {c.cta}
          </Button>
        }
      >
        <Stack spacing={4}>
          {c.detail.map((f) => (
            <Field key={f.label} label={f.label} value={f.value} />
          ))}
          <Divider />
          <Typography variant='overline' color='text.secondary'>
            {c.stateLabel}
          </Typography>
          {c.state.map((f) => (
            <Field key={f.label} label={f.label} value={f.value} />
          ))}
          <Divider />
          <Stack spacing={1}>
            <Typography variant='overline' color='text.secondary'>
              {c.noteLabel}
            </Typography>
            <Typography variant='body1'>{c.note}</Typography>
          </Stack>
        </Stack>
      </Drawer>
    </ShortlistPage>
  );
};
B2RowDrawer.displayName = 'B2RowDrawer';

// A1 — today's siting, for comparison: the real care search, shortlist in the
// narrow right-hand rail. The actual component, not a redraw — the rival has
// to be the design as it exists.
export const A1ShortlistInRail = () => <CareSearch embedded />;
A1ShortlistInRail.displayName = 'A1ShortlistInRail';

// The email chrome. Owns its own look on purpose and shares nothing with the
// rest of the flow: this lands in the provider's mail client, not in a
// KareHero surface, so brand type and colour here would be a lie about where
// the provider is. Black on white, system font, a plain text link. Colours are
// MUI's common/grey tokens rather than the KareHero palette for the same
// reason — the design system does not own this rectangle.
//
// Exported because the CAM preview of an outbound send renders inside it too: a
// CAM reading what a provider will get should be reading it in the rectangle the
// provider reads it in, and a second copy of this chrome would be free to drift.
export const EmailClient = ({
  subject,
  from,
  fromAddress,
  to,
  received,
  children,
}: {
  subject: string;
  from: string;
  fromAddress: string;
  to: string;
  received: string;
  children: ReactNode;
}) => (
  <Stack
    sx={{
      height: '100%',
      bgcolor: 'common.white',
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      color: 'common.black',
    }}
  >
    <Stack
      sx={{
        px: 6,
        py: 4,
        borderBottom: 1,
        borderColor: 'grey.300',
        flexShrink: 0,
      }}
      spacing={1}
    >
      <Typography sx={{ fontSize: 24 }} color='inherit'>
        {subject}
      </Typography>
      <Stack sx={{ justifyContent: 'space-between' }} direction='row'>
        <Typography sx={{ fontSize: 14 }} color='grey.700'>
          {from} &lt;{fromAddress}&gt; to {to}
        </Typography>
        <Typography sx={{ fontSize: 14 }} color='grey.700'>
          {received}
        </Typography>
      </Stack>
    </Stack>
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', px: 6, py: 5 }}>
      {children}
    </Box>
  </Stack>
);

// F2 — the email. Everything said here is outside the paywall, so the amount
// of lead detail in it is the whole question. It is also the care request going
// out, which makes it the thing that replaced the phone call.
export const F2Email = ({
  onView,
}: {
  // Where the link goes. Optional because this frame has two homes: on the
  // exploration page it is a picture of an email, where a link that navigated
  // would take the reader out of the writeup, and on the provider stage it is a
  // step you take, where a dead link is the thing that isn't working.
  onView?: () => void;
} = {}) => {
  const c = FLOW.email;
  return (
    <EmailClient
      subject={c.subject}
      from={c.fromName}
      fromAddress={c.fromAddress}
      to={c.to}
      received={c.received}
    >
      <Stack sx={{ maxWidth: 720 }} spacing={3}>
        <Typography color='inherit'>{c.greeting}</Typography>
        {c.body.map((p) => (
          <Typography key={p} color='inherit'>
            {p}
          </Typography>
        ))}
        {/* One link. The no used to sit under it, answerable without opening the
            lead at all, and that was the problem: a no given before the profile
            has been read is a guess, and it costs us the provider for a request
            they might well have been able to take. So the email asks one thing,
            and the no lives on the page they land on. */}
        <Stack spacing={1}>
          <Typography
            component='a'
            href='#'
            color='inherit'
            onClick={(e: MouseEvent) => {
              e.preventDefault();
              onView?.();
            }}
            sx={{
              textDecoration: 'underline',
              cursor: onView ? 'pointer' : 'default',
            }}
          >
            {c.cta}
          </Typography>
        </Stack>
        <Stack>
          {c.signOff.map((l) => (
            <Typography key={l} color='inherit'>
              {l}
            </Typography>
          ))}
        </Stack>
        <Divider />
        <Stack spacing={0.5}>
          <Typography sx={{ fontSize: 13 }} color='grey.700'>
            {c.foot}
          </Typography>
          <Typography
            component='a'
            href='#'
            color='grey.700'
            sx={{ fontSize: 13, textDecoration: 'underline' }}
          >
            {c.unsubscribe}
          </Typography>
        </Stack>
      </Stack>
    </EmailClient>
  );
};
F2Email.displayName = 'F2Email';

// The price rail, shared by the two halves of the paywall page. F4 and F5 are
// one scroll in the real thing, so the rail has to be the same object in both
// or the frames read as two pages.
const PriceRail = ({
  onUnlock,
  onDecline,
}: {
  onUnlock?: () => void;
  onDecline?: () => void;
}) => {
  const c = FLOW.paywall;
  return (
    <Paper variant='outlined' sx={{ width: 420, flexShrink: 0, p: 5 }}>
      <Stack spacing={3}>
        <Typography variant='h1'>{c.price}</Typography>
        <Button ariaLabel={c.cta} size='lg' isFullWidth onPress={onUnlock}>
          {c.cta}
        </Button>
        {/* The no, on the first screen where a provider knows enough to give one.
            Under the price rather than beside it: the two are not a pair of equal
            options, and a no that looks like the other half of a choice invites
            one from a provider who has not read the lead yet. */}
        <Button
          ariaLabel={FLOW.answers.decline}
          variant='link'
          size='md'
          isFullWidth
          onPress={onDecline}
        >
          {FLOW.answers.decline}
        </Button>
      </Stack>
    </Paper>
  );
};

// F3 — the paywall itself. Visible enough to judge fit, blurred where the PII
// is. The split between the two columns is what Ling's field list decides.
export const F3Paywall = ({
  onUnlock,
  onDecline,
}: {
  onUnlock?: () => void;
  onDecline?: () => void;
} = {}) => {
  const c = FLOW.paywall;
  return (
    <Page title={c.title} subtitle={c.kicker}>
      <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={5}>
        <Paper variant='outlined' sx={{ flex: 1, minWidth: 0, p: 5 }}>
          <Stack spacing={4}>
            <Stack spacing={1}>
              <Typography variant='h4'>{c.cardTitle}</Typography>
              <Typography variant='body1' color='text.secondary'>
                {c.cardSubtitle}
              </Typography>
            </Stack>
            {c.visible.map((f) => (
              <Field key={f.label} label={f.label} value={f.value} />
            ))}
            <Divider />
            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
              <Icon icon={'eye-slash' as never} fontSize={18} />
              <Typography variant='overline' color='text.secondary'>
                {c.hiddenLabel}
              </Typography>
            </Stack>
            <Blurred>
              <Stack spacing={4}>
                {c.hidden.map((f) => (
                  <Field key={f.label} label={f.label} value={f.value} />
                ))}
              </Stack>
            </Blurred>
          </Stack>
        </Paper>

        <PriceRail onUnlock={onUnlock} onDecline={onDecline} />
      </Stack>
    </Page>
  );
};
F3Paywall.displayName = 'F3Paywall';

// The checkout, as the rail on the answers screen rather than a screen of its
// own. Answering and paying are one decision — the provider is deciding whether
// this lead is worth £48 while they read the questions — and a page break in the
// middle of it invited them to leave. So the itemised total sits beside the
// answers and the button under it is the payment.
const CheckoutRail = ({
  onPay,
  onDecline,
}: {
  onPay?: () => void;
  onDecline?: () => void;
}) => {
  const c = FLOW.checkout;
  return (
    <Paper variant='outlined' sx={{ width: 420, flexShrink: 0, p: 5 }}>
      <Stack spacing={3}>
        {c.lines.map((l) => (
          <Stack
            sx={{ justifyContent: 'space-between' }}
            key={l.label}
            direction='row'
          >
            <Typography variant='body1'>{l.label}</Typography>
            <Typography variant='body1'>{l.value}</Typography>
          </Stack>
        ))}
        <Divider />
        <Stack sx={{ justifyContent: 'space-between' }} direction='row'>
          <Typography variant='h4'>{c.total.label}</Typography>
          <Typography variant='h4'>{c.total.value}</Typography>
        </Stack>
        {/* Terms, as a fact about the button rather than a box to tick. Directly
            above it, so it is read where it applies. */}
        <Typography variant='body2' color='text.secondary'>
          {c.accept.before}
          <Typography
            component='a'
            href='#'
            variant='body2'
            color='primary'
            sx={{ textDecoration: 'underline' }}
          >
            {c.accept.link}
          </Typography>
          {c.accept.after}
        </Typography>
        <Button ariaLabel={c.cta} size='lg' isFullWidth onPress={onPay}>
          {c.cta}
        </Button>
        {/* The no, directly under the payment and inside the same rail — same
            place it sits on the blurred profile. One act, one position: a
            provider who works out they can't help while answering shouldn't
            have to find it somewhere new. */}
        <Button
          ariaLabel={FLOW.answers.decline}
          variant='link'
          size='md'
          isFullWidth
          onPress={onDecline}
        >
          {FLOW.answers.decline}
        </Button>
      </Stack>
    </Paper>
  );
};

// F4 — the callback, as a page. The 12 Aug spec turned the paywall from a gate
// into the replacement for the phone call, so the thing CAM used to establish on
// the phone is settled here: can this provider take this request. It is answered
// by paying, against the request read back. We collect nothing about the lead we
// already hold, and nothing is kept as a standing profile, because profiles rot.
//
// Which leaves the contact fields as the only inputs on the page, and a returning
// provider with none: their F4 is the request read back beside the checkout.
export const F4Answers = ({
  onPay,
  onDecline,
  isReturning,
}: {
  onPay?: () => void;
  onDecline?: () => void;
  /**
   * The second lead and every one after it. Drops the contact fields, which is now
   * the whole difference: what is left is the request read back beside the
   * checkout, and it is this request, so nothing on the page is reused from last
   * time.
   */
  isReturning?: boolean;
} = {}) => {
  const c = FLOW.answers;
  return (
    <Page title={c.title}>
      <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={5}>
        <Paper variant='outlined' sx={{ flex: 1, minWidth: 0, p: 5 }}>
          <Stack spacing={5}>
            <Stack spacing={1}>
              <Typography variant='h4'>{c.cardTitle}</Typography>
              <Typography variant='body1' color='text.secondary'>
                {c.intro}
              </Typography>
            </Stack>

            {/* The details block, at the top, above the request it is answering.
                Contact on a first lead only — a named contact is close to a
                fixed fact, which is what makes asking once reasonable. The rate
                is in the same group but outside that gate, because a rate moves:
                gating it behind the first lead would make it uncorrectable
                exactly when it starts to be wrong. On a second lead this block
                is the rate on its own. */}
            <Stack spacing={3}>
              {!isReturning &&
                c.firstTime.fields.map((f) => (
                  <TextField
                    key={f.label}
                    label={f.label}
                    placeholder={f.placeholder}
                    variant='filled'
                    fullWidth
                  />
                ))}

              {/* Amount and period, side by side. The period is picked rather
                  than assumed: the same field carries an hourly quote and a
                  monthly live-in package, and `price` in care search is one
                  string holding whichever shape it arrived in. */}
              <Stack spacing={1}>
                <Stack
                  sx={{ alignItems: 'flex-start' }}
                  direction='row'
                  spacing={2}
                >
                  <TextField
                    label={c.rate.label}
                    defaultValue={c.rate.defaultValue}
                    variant='filled'
                    fullWidth
                  />
                  {/* Width on a Box rather than sx on the field: the select
                      needs a floor so 'Hourly' and 'Monthly' don't resize it,
                      and that is a layout fact about the row. */}
                  <Box sx={{ width: 180, flexShrink: 0 }}>
                    <TextField
                      select
                      label={c.rate.periodLabel}
                      defaultValue={c.rate.defaultPeriod}
                      variant='filled'
                      fullWidth
                    >
                      {c.rate.periods.map((p) => (
                        <MenuItem key={p} value={p}>
                          {p}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Stack>
                <Typography variant='body2' color='text.secondary'>
                  {c.rate.hint}
                </Typography>
              </Stack>
            </Stack>

            {/* Always shown now: there is an input above it on every lead, so
                the rule separating what we are asking for from what we already
                have is always earning its place. */}
            <Divider />

            {/* The request, as it came through on the screen before. Read from
                the same list F3 renders, so what a provider confirms here and
                what they were shown there cannot drift apart — and rendered
                with the same Field, at the same spacing, so it reads as the
                lead they have already seen rather than a second version of it.

                No inputs, and no confirm box either. Paying is the yes: a tick
                above the pay button asked the same question twice, and the no is
                the decline in the rail rather than an unticked box. */}
            <Stack spacing={4}>
              <Typography variant='overline' color='text.secondary'>
                {c.confirmLabel}
              </Typography>
              {FLOW.paywall.visible.map((f) => (
                <Field key={f.label} label={f.label} value={f.value} />
              ))}
            </Stack>
          </Stack>
        </Paper>

        {/* The one-tap no rides in the rail, under the payment. A no is data
            too, and it has to be as easy as the yes. */}
        <CheckoutRail onPay={onPay} onDecline={onDecline} />
      </Stack>
    </Page>
  );
};
F4Answers.displayName = 'F4Answers';

// F6 — unlocked. The moment value is delivered, and the first time the
// provider has anything they could act on outside KareHero.
export const F6Unlocked = () => {
  const c = FLOW.unlocked;
  return (
    <Page title={c.title} subtitle={c.sub}>
      <Paper variant='outlined' sx={{ maxWidth: 720, p: 5 }}>
        <Stack spacing={4}>
          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
            <Icon icon={'circle-check' as never} fontSize={22} />
            <Typography variant='h4'>{c.note}</Typography>
          </Stack>
          <Divider />
          {/* The request first, then what payment released. Same order the
              paywall had them in, and the same lines: the group above is
              `paywall.visible`, so a provider reads the profile they judged
              back with the contact details attached to it. */}
          <Stack spacing={4}>
            <Typography variant='overline' color='text.secondary'>
              {c.requestLabel}
            </Typography>
            {FLOW.paywall.visible.map((f) => (
              <Field key={f.label} label={f.label} value={f.value} />
            ))}
          </Stack>
          <Divider />
          <Stack spacing={4}>
            <Typography variant='overline' color='text.secondary'>
              {c.unlockedLabel}
            </Typography>
            {c.items.map((f) => (
              <Field key={f.label} label={f.label} value={f.value} />
            ))}
          </Stack>
          <Divider />
          {/* The invoice is dull and load-bearing: without one, a bookkeeper is
              a reason not to pay. No line promising the lead stays here — where it
              lives after checkout is not decided, and a promise about it was the
              screen deciding on our behalf. */}
          <Stack direction='row' spacing={2}>
            <Button ariaLabel={c.cta} size='lg'>
              {c.cta}
            </Button>
            <Button ariaLabel={c.invoice} variant='secondary' size='lg'>
              {c.invoice}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Page>
  );
};
F6Unlocked.displayName = 'F6Unlocked';

// F7 — the member's push. Their side of a payment they never see: from here it
// reads as a provider becoming available, nothing more.
export const F7Push = ({
  title,
  body,
}: {
  // The words, when they are not the provider-has-paid ones. The lock screen and
  // the clock are the same picture whichever notification landed on it, so the
  // frame is one component and the message is the argument.
  title?: string;
  body?: string;
} = {}) => {
  const c = { ...FLOW.push, ...(title && { title }), ...(body && { body }) };
  return (
    <Stack sx={{ height: '100%', bgcolor: 'custom.neutral.900' }}>
      <StatusBar />
      <Stack sx={{ alignItems: 'center', pt: 7, pb: 6, flexShrink: 0 }}>
        <Typography
          sx={{ fontSize: 72 }}
          variant='h1'
          color='custom.neutral.50'
        >
          {c.time}
        </Typography>
        <Typography variant='body1' color='custom.neutral.300'>
          {c.date}
        </Typography>
      </Stack>
      <Box sx={{ px: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
              <Box
                sx={{ width: 16, height: 'auto' }}
                component='img'
                src='/vector/karehero-logo.png'
                alt=''
              />
              <Typography
                sx={{ flex: 1 }}
                variant='caption'
                color='text.secondary'
              >
                {c.app}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {c.when}
              </Typography>
            </Stack>
            <Typography variant='subtitle2'>{c.title}</Typography>
            <Typography variant='body2'>{c.body}</Typography>
          </Stack>
        </Paper>
      </Box>
    </Stack>
  );
};
F7Push.displayName = 'F7Push';

// The published shortlist, restated for this flow. Secured means the family
// has agreed a placement, which hasn't happened here — a provider reaches this
// list by paying to unlock the lead, so the honest badge is Available. Mapped
// at the call site rather than edited into the shared fixtures, which belong
// to Shortlists V1.
const PAYWALL_SHORTLIST = {
  ...SHORTLISTS[0],
  providers: SHORTLISTS[0].providers.map((p) =>
    p.status === 'Secured' ? { ...p, status: 'Available' as const } : p,
  ),
};

// F8 — the shortlist they land on. The real published-shortlist design, not a
// redraw: the point of the frame is that nothing about this screen changes,
// only who is allowed onto it.
export const F8MemberShortlist = () => (
  <ShortlistView
    shortlist={PAYWALL_SHORTLIST}
    onBack={() => {}}
    onOpenProvider={() => {}}
    onToggleSecured={() => {}}
  />
);
F8MemberShortlist.displayName = 'F8MemberShortlist';

// F9 — the same moment for the CAM, on the real screen: the Shortlists tab after
// the list has published. One provider paid and is visible to the family, one is
// still deciding and is absent, the standard provider went over without a
// paywall. Same surface as F1, later — which is the point, and also where the
// state model runs out. See the frame's brief.
export const F9CamShortlist = () => (
  <CamCircle
    tab='shortlist'
    shortlist={{ scenario: 'firstPaid', published: true }}
  />
);
F9CamShortlist.displayName = 'F9CamShortlist';

// F11 — the lead is over. An expired link and a client who has already arranged
// their care both land here, because from the provider's side they are the same
// fact and there is nothing to do about either.
//
// One line in the page header where the lead's own title goes, and the charge line
// under it. No icon and no button: there is nothing here for them to do, and
// dressing that up would suggest there is.
export const F11Timeout = () => (
  <Page title={FLOW.timeout.title} subtitle={FLOW.timeout.note}>
    {null}
  </Page>
);
F11Timeout.displayName = 'F11Timeout';

// F12 — the unpaid provider, put in front of the family anyway. It stopped being a
// branch of Publish when publishing became the send: nobody has paid at that
// moment, so there is nothing to hold back and nothing to tick. The decision moved
// to where it actually gets made — one row, on a published list, after the wait has
// gone on long enough. The real screen, on a list nobody has paid on.
export const F12PublishUnpaid = () => (
  <CamCircle
    tab='shortlist'
    shortlist={{ scenario: 'allQuiet', published: true }}
  />
);
F12PublishUnpaid.displayName = 'F12PublishUnpaid';

// Every state the paywall page has that is not the open one. One component, so
// they can't drift apart in tone: each is a full screen with its own words, and
// none of them is a banner bolted onto the open page or a generic error. What
// varies is only the words and whether there is anything to do.

// S1 — the client has placed. Not the same as full, and it cannot read the same:
// this is a wasted trip and the provider will feel it.

// S2 — the card was declined or the session dropped. Their place and their
// answers survive, because a declined card should not cost anyone a lead.
// The provider's payment did not complete. Title only in the header — no icon
// and no kicker, matching the expired screen — then what it means for them and
// the way back. No design note and no divider under it: this is the screen, not
// a card about the screen.
export const S2PaymentFailed = () => {
  const c = FLOW.states.failed;
  return (
    <Page title={c.title}>
      <Paper variant='outlined' sx={{ maxWidth: 620, p: 5 }}>
        <Stack spacing={3}>
          {/* Said again on the card rather than left to the header. The header is
              page chrome and reads as such; the thing that went wrong belongs
              with the explanation of it. */}
          <Typography variant='h4'>{c.title}</Typography>
          {c.body.map((line) => (
            <Typography key={line} variant='body1'>
              {line}
            </Typography>
          ))}
          <Box>
            <Button ariaLabel={c.cta} size='lg'>
              {c.cta}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Page>
  );
};
S2PaymentFailed.displayName = 'S2PaymentFailed';

// S4 — the no, answered. Same shape as S2: title in the header, said again on
// the card, then what it means for them. Nothing to submit and no button, because
// there is no reason picker behind it — the tap was the whole interaction, and a
// screen that asks one more question is a screen that makes the next provider
// think twice about telling us anything.
export const S4Declined = () => {
  const c = FLOW.states.declined;
  return (
    <Page title={c.title}>
      <Paper variant='outlined' sx={{ maxWidth: 620, p: 5 }}>
        <Stack spacing={3}>
          <Typography variant='h4'>{c.title}</Typography>
          {c.body.map((line) => (
            <Typography key={line} variant='body1'>
              {line}
            </Typography>
          ))}
        </Stack>
      </Paper>
    </Page>
  );
};
S4Declined.displayName = 'S4Declined';

// S3 — an old link. An explanation and a way back to a human, never a 404.
// Only for a link that has expired or that we do not recognise: a provider who
// has already paid coming back to their own link is not an error state, so they
// land on F6Unlocked instead. Nothing new to draw for that case, which is the
// point — the lead screen is where the lead lives.
